/**
 * Cross-Tab Auth Sync
 *
 * Singleton class that manages cross-tab authentication synchronization.
 * Uses BroadcastChannel for reliable communication with localStorage fallback.
 *
 * Key features:
 * - Observer pattern: AuthProvider calls authStateChanged(), class handles broadcasting
 * - Ping-pong prevention: isReactingToBroadcast flag prevents infinite loops
 * - Source-aware: tabId in messages allows filtering own messages
 * - Token refresh coordination: Web Locks API (primary) or localStorage (fallback)
 */

import { logger } from '../logger';
import { featureFlags } from '@config/featureFlags';
import { TokenRefreshLock } from './TokenRefreshLock';
import {
  CrossTabMessageType,
  CrossTabMessage,
  AuthStateChangeOptions,
  AuthStateCallback,
  TokenRefreshCallback,
  MagicLinkClaimedCallback,
} from './types';
import { amplitude, isAmplitudeInitialized } from '@lib/amplitude';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';

/**
 * Track analytics event for token refresh coordination.
 * This is a lightweight helper for non-React contexts.
 * Events are only tracked in production to avoid polluting analytics data.
 */
function trackTokenRefreshEvent(eventName: string, properties: Record<string, unknown>): void {
  // Skip in development/test environments
  if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') {
    return;
  }

  if (!isAmplitudeInitialized()) {
    return;
  }

  try {
    amplitude.track(eventName, properties);
  } catch (error) {
    // Don't let analytics errors affect auth flow
    logger.debug('Cross-tab sync: Failed to track analytics event', error);
  }
}

const BROADCAST_CHANNEL_NAME = 'meetball_auth_sync';

export class CrossTabAuthSync {
  private static instance: CrossTabAuthSync | null = null;

  // Unique ID for this tab - used to filter own messages
  private readonly tabId = crypto.randomUUID();

  // Current known auth state
  private isAuthenticated = false;

  // Flag: are we currently reacting to a broadcast from another tab?
  // When true, we won't re-broadcast (prevents ping-pong)
  private isReactingToBroadcast = false;

  // Flag: is another tab currently refreshing tokens?
  // Tracked via BroadcastChannel messages (for Web Locks path)
  private otherTabRefreshingFlag = false;

  // Communication channel
  private broadcastChannel: BroadcastChannel | null = null;

  // Event subscribers
  private signedOutCallbacks = new Set<AuthStateCallback>();
  private signedInCallbacks = new Set<AuthStateCallback>();
  private tokenRefreshedCallbacks = new Set<TokenRefreshCallback>();
  private magicLinkClaimedCallbacks = new Set<MagicLinkClaimedCallback>();

  // Token refresh lock
  private refreshLock: TokenRefreshLock;

  // Bound event handler - stored for proper cleanup
  private boundHandleStorageEvent: (event: StorageEvent) => void;

  private constructor() {
    this.refreshLock = new TokenRefreshLock({ enableWebLocks: featureFlags.useWebLocks });

    this.boundHandleStorageEvent = this.handleStorageEvent.bind(this);
    this.initialize();
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): CrossTabAuthSync {
    if (!CrossTabAuthSync.instance) {
      CrossTabAuthSync.instance = new CrossTabAuthSync();
    }
    return CrossTabAuthSync.instance;
  }

  /**
   * Reset the singleton instance (for testing only)
   */
  static resetInstance(): void {
    CrossTabAuthSync.instance = null;
  }

  /**
   * Initialize the sync system
   */
  private initialize(): void {
    if (typeof window === 'undefined') return;

    // Initialize BroadcastChannel if available
    if ('BroadcastChannel' in window) {
      try {
        this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        this.broadcastChannel.onmessage = this.handleMessage.bind(this);
        logger.debug('Cross-tab sync: BroadcastChannel initialized');
      } catch (e) {
        logger.warn('Cross-tab sync: BroadcastChannel not available', e);
      }
    }

    // Also listen for storage events as fallback
    window.addEventListener('storage', this.boundHandleStorageEvent);

    // Clean up stale locks (only relevant for localStorage fallback)
    this.refreshLock.cleanupStaleLock();

    logger.debug('Cross-tab sync: Initialized');
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (typeof window === 'undefined') return;

    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }

    window.removeEventListener('storage', this.boundHandleStorageEvent);

    this.signedOutCallbacks.clear();
    this.signedInCallbacks.clear();
    this.tokenRefreshedCallbacks.clear();
    this.magicLinkClaimedCallbacks.clear();

    CrossTabAuthSync.instance = null;
  }

  /**
   * Notify the sync system that auth state has changed.
   * This is the main integration point for AuthProvider.
   *
   * @param isAuthenticated - The new auth state
   * @param options.isUserInitiated - True if user explicitly signed in/out
   */
  authStateChanged(isAuthenticated: boolean, options: AuthStateChangeOptions = {}): void {
    const wasAuthenticated = this.isAuthenticated;
    this.isAuthenticated = isAuthenticated;

    // Don't broadcast if:
    // 1. State didn't actually change
    if (wasAuthenticated === isAuthenticated) {
      return;
    }

    // 2. We're reacting to another tab's broadcast (prevents ping-pong)
    if (this.isReactingToBroadcast) {
      logger.debug('Cross-tab sync: Skipping broadcast (reacting to other tab)');
      return;
    }

    // 3. Not user-initiated (e.g., tab loaded with existing tokens)
    if (!options.isUserInitiated) {
      logger.debug('Cross-tab sync: Skipping broadcast (not user-initiated)');
      return;
    }

    // Broadcast the change
    if (isAuthenticated) {
      this.broadcast(CrossTabMessageType.SIGNED_IN);
    } else {
      this.broadcast(CrossTabMessageType.SIGNED_OUT);
    }
  }

  /**
   * Register callback for when another tab signs out
   */
  onSignedOut(callback: AuthStateCallback): () => void {
    this.signedOutCallbacks.add(callback);
    return () => this.signedOutCallbacks.delete(callback);
  }

  /**
   * Register callback for when another tab signs in
   */
  onSignedIn(callback: AuthStateCallback): () => void {
    this.signedInCallbacks.add(callback);
    return () => this.signedInCallbacks.delete(callback);
  }

  /**
   * Register callback for when another tab refreshes tokens
   */
  onTokenRefreshed(callback: TokenRefreshCallback): () => void {
    this.tokenRefreshedCallbacks.add(callback);
    return () => this.tokenRefreshedCallbacks.delete(callback);
  }

  /**
   * Tell the other tabs in this browser that a magic-link flow is being spent
   * HERE, so a sibling door waiting on the same flow can stop waiting.
   *
   * Called at the honest moment -- the token is about to be redeemed -- and
   * never earlier: a door that is still waiting on a click has redeemed
   * nothing, and telling a sibling otherwise would have it announce a hand-off
   * that has not started. What the sibling may say off this message is bounded
   * for the same reason -- the redemption begins one statement after the send
   * and can still fail, so the copy states the CLAIM and never the sign-in;
   * see `AUTH_DOOR_COPY.claimedElsewhere`.
   *
   * A NO-OP without `BroadcastChannel`, like every other broadcast here. There
   * is deliberately no `storage`-event fallback for this type: the sibling
   * simply learns on its next status poll, a few seconds later, which is the
   * behaviour this message exists to improve on rather than to replace.
   */
  magicLinkClaimed(flowId: string): void {
    this.broadcast(CrossTabMessageType.MAGIC_LINK_CLAIMED, { flowId });
  }

  /**
   * Register callback for when another tab claims a magic-link flow. The
   * callback only ever receives a string flow id; a message without one is
   * dropped in `handleMessage`.
   */
  onMagicLinkClaimed(callback: MagicLinkClaimedCallback): () => void {
    this.magicLinkClaimedCallbacks.add(callback);
    return () => this.magicLinkClaimedCallbacks.delete(callback);
  }

  /**
   * Execute a token refresh operation with cross-tab coordination.
   * Ensures only one tab refreshes at a time.
   *
   * Uses Web Locks API for coordination (with localStorage fallback).
   * When using Web Locks, the lock auto-releases when the callback completes
   * or when the tab closes - no orphaned locks possible.
   *
   * @param refreshFn - Function that performs the actual token refresh
   * @param onOtherTabRefreshed - Optional callback when another tab completed refresh while we waited
   */
  async withRefreshLock<T>(
    refreshFn: () => Promise<T>,
    onOtherTabRefreshed?: () => T | Promise<T>,
  ): Promise<T> {
    // Track if another tab completed refresh while we wait for the lock
    let tokenRefreshedWhileWaiting = false;
    const unsubscribe = this.onTokenRefreshed(() => {
      tokenRefreshedWhileWaiting = true;
    });

    const lockType = this.refreshLock.isUsingWebLocks() ? 'web_locks' : 'local_storage';
    const startTime = Date.now();

    try {
      return await this.refreshLock.withLock(async () => {
        const waitTimeMs = Date.now() - startTime;

        // Track if we had to wait for the lock (indicates real contention)
        if (waitTimeMs > 50) {
          trackTokenRefreshEvent(ANALYTICS_EVENTS.token_refresh_lock_waited, {
            lock_type: lockType,
            wait_time_ms: waitTimeMs,
          });
        }

        // Reset otherTabRefreshingFlag since we now hold the lock.
        // This handles the edge case where another tab crashed mid-refresh:
        // the Web Lock auto-released but no TOKEN_REFRESHED/FAILED was broadcast.
        this.otherTabRefreshingFlag = false;

        // Check if we should use cached result from other tab
        // This can happen when:
        // 1. Web Locks: We waited in queue while another tab held the lock
        // 2. localStorage: We polled while another tab was refreshing
        if (tokenRefreshedWhileWaiting && onOtherTabRefreshed) {
          logger.debug('Cross-tab sync: Using token refreshed by another tab');
          trackTokenRefreshEvent(ANALYTICS_EVENTS.token_refresh_by_other_tab, {
            lock_type: lockType,
          });
          return onOtherTabRefreshed();
        }

        // We're the refresher - proceed with the refresh
        logger.debug('Cross-tab sync: Acquired lock, proceeding with refresh');
        this.broadcast(CrossTabMessageType.TOKEN_REFRESH_STARTED);

        try {
          const result = await refreshFn();
          this.broadcast(CrossTabMessageType.TOKEN_REFRESHED);
          return result;
        } catch (error) {
          // Broadcast failure so other tabs can reset their otherTabRefreshingFlag
          // and attempt their own refresh
          this.broadcast(CrossTabMessageType.TOKEN_REFRESH_FAILED);
          logger.error('Cross-tab sync: Token refresh failed', error);
          throw error;
        }
      });
    } finally {
      unsubscribe();
    }
  }

  /**
   * Check if another tab is currently refreshing tokens.
   *
   * For Web Locks: Uses internal flag tracked via BroadcastChannel messages.
   * For localStorage: Checks if another tab holds the lock.
   */
  isOtherTabRefreshing(): boolean {
    if (this.refreshLock.isUsingWebLocks()) {
      // Web Locks: We track via BroadcastChannel messages
      return this.otherTabRefreshingFlag;
    }
    // localStorage: Check lock directly
    return this.refreshLock.isOtherTabHoldingLock();
  }

  /**
   * Check if Web Locks API is being used for token refresh coordination.
   */
  isUsingWebLocks(): boolean {
    return this.refreshLock.isUsingWebLocks();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private methods
  // ─────────────────────────────────────────────────────────────────────────────

  private handleMessage(event: MessageEvent<CrossTabMessage>): void {
    const { type, tabId } = event.data;

    // Ignore our own messages
    if (tabId === this.tabId) {
      logger.debug(`Cross-tab sync: Ignoring own message (type: ${type})`);
      return;
    }

    // Mark that we're reacting to a broadcast (prevents re-broadcast)
    this.isReactingToBroadcast = true;

    try {
      switch (type) {
        case CrossTabMessageType.SIGNED_OUT:
          logger.debug(`Cross-tab sync: Another tab (${tabId}) signed out`);
          this.isAuthenticated = false;
          this.signedOutCallbacks.forEach(cb => {
            try {
              cb();
            } catch (e) {
              logger.error('Cross-tab sync: Error in signed-out callback', e);
            }
          });
          break;

        case CrossTabMessageType.SIGNED_IN:
          logger.debug(`Cross-tab sync: Another tab (${tabId}) signed in`);
          this.isAuthenticated = true;
          this.signedInCallbacks.forEach(cb => {
            try {
              cb();
            } catch (e) {
              logger.error('Cross-tab sync: Error in signed-in callback', e);
            }
          });
          break;

        case CrossTabMessageType.TOKEN_REFRESH_STARTED:
          logger.debug(`Cross-tab sync: Another tab (${tabId}) started token refresh`);
          this.otherTabRefreshingFlag = true;
          break;

        case CrossTabMessageType.TOKEN_REFRESHED:
          logger.debug(`Cross-tab sync: Another tab (${tabId}) refreshed tokens`);
          this.otherTabRefreshingFlag = false;
          this.tokenRefreshedCallbacks.forEach(cb => {
            try {
              cb();
            } catch (e) {
              logger.error('Cross-tab sync: Error in token-refreshed callback', e);
            }
          });
          break;

        case CrossTabMessageType.TOKEN_REFRESH_FAILED:
          // Reset the flag so this tab can attempt its own refresh
          logger.debug(`Cross-tab sync: Another tab (${tabId}) failed token refresh`);
          this.otherTabRefreshingFlag = false;
          break;

        case CrossTabMessageType.MAGIC_LINK_CLAIMED: {
          // Read off `event.data` rather than the destructure above, which
          // only takes the two fields every message carries.
          const { flowId } = event.data;
          // A message with no flow id, or one that is not a string, describes
          // nothing this tab can act on. Dropped here so no subscriber -- all
          // of them typed `(flowId: string) => void` -- is ever handed
          // `undefined`.
          if (typeof flowId !== 'string') {
            logger.debug(`Cross-tab sync: Ignoring MAGIC_LINK_CLAIMED with no flow id`);
            break;
          }
          logger.debug(`Cross-tab sync: Another tab (${tabId}) claimed a magic link`);
          this.magicLinkClaimedCallbacks.forEach(cb => {
            try {
              cb(flowId);
            } catch (e) {
              logger.error('Cross-tab sync: Error in magic-link-claimed callback', e);
            }
          });
          break;
        }
      }
    } finally {
      // Reset after a microtask to allow the caller's response to complete
      queueMicrotask(() => {
        this.isReactingToBroadcast = false;
      });
    }
  }

  private handleStorageEvent(event: StorageEvent): void {
    // Check if it's a Logto token change
    if (event.key?.startsWith('logto:') && event.key.includes(':refreshToken')) {
      if (event.newValue && event.newValue !== event.oldValue) {
        logger.debug('Cross-tab sync: Detected refresh token change via storage event');
        this.tokenRefreshedCallbacks.forEach(cb => {
          try {
            cb();
          } catch (e) {
            logger.error('Cross-tab sync: Error in token-refreshed callback', e);
          }
        });
      }

      // Sign-in/sign-out detection via storage events is only used as a fallback
      // when BroadcastChannel is unavailable. When BroadcastChannel is available,
      // we use explicit broadcasts to avoid double-triggering callbacks.
      if (!this.broadcastChannel) {
        // Detect sign-in: refresh token appeared
        if (!event.oldValue && event.newValue) {
          logger.debug('Cross-tab sync: Detected sign-in via storage event (fallback)');
          this.signedInCallbacks.forEach(cb => {
            try {
              cb();
            } catch (e) {
              logger.error('Cross-tab sync: Error in signed-in callback', e);
            }
          });
        }

        // Detect sign-out: refresh token removed
        if (event.oldValue && !event.newValue) {
          logger.debug('Cross-tab sync: Detected sign-out via storage event (fallback)');
          this.signedOutCallbacks.forEach(cb => {
            try {
              cb();
            } catch (e) {
              logger.error('Cross-tab sync: Error in signed-out callback', e);
            }
          });
        }
      }
    }
  }

  /**
   * `extra` carries the per-type payload, and the callers pass a FIELD BAG
   * rather than a whole message: `tabId` and `timestamp` are this class's to
   * stamp, and a call site that could supply its own would eventually supply
   * the wrong one and defeat the own-message filter. The bag is spread FIRST
   * so the stamp always wins: the parameter type does not stop a wider object
   * held in a variable, because excess-property checks apply to literals only.
   */
  private broadcast(
    type: CrossTabMessageType,
    extra?: Partial<Omit<CrossTabMessage, 'type' | 'tabId' | 'timestamp'>>,
  ): void {
    if (this.broadcastChannel) {
      const message: CrossTabMessage = {
        ...extra,
        type,
        tabId: this.tabId,
        timestamp: Date.now(),
      };
      this.broadcastChannel.postMessage(message);
      logger.debug(`Cross-tab sync: Broadcasted ${type} to other tabs`);
    }
  }
}
